import React from 'react';

/**
 * Genre pill filter — mirrors PlatformFilter styling. Multi-select:
 * users tap multiple pills to widen the picks (OR logic). Tapping "All"
 * clears every selection. Tapping an already-active pill toggles it off.
 *
 * "Anime" is intentionally split out from "Animated": anime is detected
 * via original_language==='ja' + Animation genre; western animation
 * matches Animation without a language constraint.
 */
export const GENRES = [
  { id: 'all', name: 'All' },
  { id: 'action', name: 'Action' },
  { id: 'comedy', name: 'Comedy' },
  { id: 'thriller', name: 'Thriller' },
  { id: 'horror', name: 'Horror' },
  { id: 'sci-fi', name: 'Sci-Fi' },
  { id: 'romance', name: 'Romance' },
  { id: 'anime', name: 'Anime' },
  { id: 'animated', name: 'Animated' },
];

const GenreFilter = ({ selectedGenres = [], onToggleGenre, onClearGenres }) => {
  const allActive = !selectedGenres || selectedGenres.length === 0;
  return (
    <div className="flex flex-wrap gap-3 items-center" data-testid="genre-filter">
      {GENRES.map((genre) => {
        const isAll = genre.name === 'All';
        const isActive = isAll ? allActive : selectedGenres.includes(genre.name);
        const handleClick = () => {
          if (isAll) {
            onClearGenres?.();
          } else {
            onToggleGenre?.(genre.name);
          }
        };
        return (
          <button
            key={genre.id}
            data-testid={`genre-filter-${genre.id}`}
            onClick={handleClick}
            aria-pressed={isActive}
            className={`px-4 py-2 rounded-full border transition-all text-sm font-mono uppercase tracking-wider
              ${isActive
                ? 'bg-black text-white border-black'
                : 'border-gray-700 bg-white text-black hover:border-black'
              }
            `}
          >
            {genre.name}
          </button>
        );
      })}
      {selectedGenres.length > 0 && (
        <span
          className="text-xs font-mono uppercase text-gray-600 ml-1"
          data-testid="genre-filter-count"
        >
          {selectedGenres.length} selected
        </span>
      )}
    </div>
  );
};

/**
 * Match a single genre key against an item's metadata.
 * `original_language` is only known for OTT releases; theatrical trailers
 * don't have it so anime detection there falls back to explicit keyword.
 */
export const matchesGenreKey = (item, genreName) => {
  const genres = (item?.genres || '').toLowerCase();
  const origLang = (item?.original_language || '').toLowerCase();
  const language = (item?.language || '').toLowerCase();
  const key = (genreName || '').toLowerCase();

  switch (key) {
    case 'sci-fi':
      return genres.includes('science fiction') ||
             genres.includes('sci-fi') ||
             genres.includes('sci fi');
    case 'anime':
      return (
        (genres.includes('animation') && (origLang === 'ja' || language === 'japanese')) ||
        genres.includes('anime')
      );
    case 'animated':
      return genres.includes('animation');
    default:
      return genres.includes(key);
  }
};

/**
 * Multi-select genre match — OR logic. Empty array = no filter (show all).
 * A release matches if it satisfies ANY of the selected genres, which
 * "widens" the discovery net when the user taps multiple pills.
 */
export const matchesAnyGenre = (item, selectedGenres) => {
  if (!selectedGenres || selectedGenres.length === 0) return true;
  return selectedGenres.some((g) => matchesGenreKey(item, g));
};

/**
 * Return the alphabetically-first matched genre for a release, used as
 * the sort key when the user picks "Sort: Genre". Falls back to the
 * first raw genre token, or an empty string so untagged items sink to
 * the end when sorted ascending.
 */
export const primaryGenreOf = (item) => {
  const raw = (item?.genres || '').split(',').map(g => g.trim()).filter(Boolean);
  return raw.length ? raw.sort((a, b) => a.localeCompare(b))[0].toLowerCase() : '';
};

export default GenreFilter;
